import { OverlayModule } from '@angular/cdk/overlay';
import { APP_BASE_HREF, registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import localeSv from '@angular/common/locales/sv';
import {
  DEFAULT_CURRENCY_CODE,
  inject,
  LOCALE_ID,
  NgModule,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { environment } from '@environments/environment';
import {
  GcButtonComponent,
  GcInputFieldComponent,
  GcLoadingSpinnerComponent,
  GcModalComponent,
  GcRadioButtonComponent,
  GcSnackbarsContainerComponent,
  GcSnackbarService,
  IpoLanguageService,
  IpoStringService,
  IpoTouchCheckService,
  stringServiceFactory,
} from '@ip-only/front-end-library';
import { ApiBaseHttpInterceptor } from '@shared/api/api-base-http.interceptor';
import {
  includeBearerTokenInterceptor,
  provideKeycloak,
} from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { GroupsApiService } from './hub/api/group-api.service';
import { UserService } from './hub/api/user.service';
import { HubSidebarComponent } from './hub/pages/hub-sidebar/hub-sidebar.component';
import { provideKeycloakBearerToken } from './keycloak/keycloak-bearer-token';
import { initializeKeycloak } from './keycloak/keycloak-init';
import { FooterComponent } from './shared/components/footer/footer.component';
import { NavbarComponent } from './shared/components/navbar/navbar.component';
import { InitGoogleAnalyticsService } from './shared/google-analytics/init-google-analytics.service';
import { ProgressService } from './shared/services/progress.service';
import { SelfserviceThemeService } from './shared/services/theme.service';
import { isGlobalConnectFinland } from './shared/utils/communication-operator.util';
import sv from './strings.sv';

const strings = { sv };
const appInit = (languageService: IpoLanguageService) => () => {
  const previouslySelectedLanguage = languageService.getStoredLanguage();

  if (!previouslySelectedLanguage) {
    languageService.setLanguage(isGlobalConnectFinland() ? 'fi' : 'sv');
  } else {
    languageService.setLanguage(previouslySelectedLanguage);
  }
};

registerLocaleData(localeSv);

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    NavbarComponent,
    HubSidebarComponent,
    OverlayModule,
    GcLoadingSpinnerComponent,
    GcButtonComponent,
    GcInputFieldComponent,
    GcModalComponent,
    GcRadioButtonComponent,
    GcSnackbarsContainerComponent,
    FooterComponent,
  ],
  bootstrap: [AppComponent],
  providers: [
    provideZoneChangeDetection(),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([includeBearerTokenInterceptor])
    ),
    ...environment.providers,
    IpoLanguageService,
    GcSnackbarService,
    ProgressService,
    provideKeycloak({
      config: environment.keycloakConfig,
    }),
    provideKeycloakBearerToken(),
    {
      provide: IpoStringService,
      deps: [IpoLanguageService],
      useFactory: stringServiceFactory((() => strings)()),
    },
    provideAppInitializer(() => {
      const initializerFn = appInit(inject(IpoLanguageService));
      return initializerFn();
    }),
    provideAppInitializer(() => inject(InitGoogleAnalyticsService).start()),
    provideAppInitializer(() => inject(SelfserviceThemeService).init()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiBaseHttpInterceptor,
      multi: true,
    },
    { provide: LOCALE_ID, useValue: 'sv-SE' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'SEK' },
    { provide: APP_BASE_HREF, useValue: '/' },
    provideAppInitializer(() => {
      const initializerFn = initializeKeycloak(
        inject(Keycloak),
        inject(IpoTouchCheckService),
        inject(UserService),
        inject(GroupsApiService)
      );
      return initializerFn();
    }),
  ],
})
export class AppModule {}
